import { useState, useMemo } from 'react';
import { Box } from '@chakra-ui/react';
import { Icon, Text, Button, Colors } from '@mercator-gcp/grout';
import { useNavigate } from 'react-router-dom';
import SearchInput from 'components/elements/SearchInput';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import { Pagination } from 'components/layout/List/Paging';
import DashboardLeadsTable from 'components/widgets/Dashboard/DashboardLeadsTable';
import { useDebounce } from 'hooks/useDebounced';
import { useFilterGroupConfigLeads } from 'hooks/useFilterGroupConfigLeads';
import { useHiddenProjects } from 'hooks/useHiddenProjects';
import { usePaginate } from 'hooks/usePaginate';
import { useProjectCsvExport } from 'hooks/useProjectCsvExport';
import { useSortedProjects } from 'hooks/useSortedProjects';
import { useDashboardGroupId } from 'state/dashboard';
import { GroupRawResponse } from 'types';
import { ProjectGroupTypes } from 'utils/analytics/constants';
import { STATE_CODE_TO_LABEL } from 'utils/constants/location';
import { buildProjectMapURLWithParams } from 'utils/navigation';

const buttonStyles = {
  mr: 'eight',
};

const containerRow = {
  alignItems: 'flex-start',
  height: '100%',
  width: '100%',
  '.chakra-link': {
    textDecoration: 'none',
  },
};

const containerColumn = {
  width: '100%',
  height: '100%',
  boxSizing: 'border-box',
  gap: 'padding_16',
};

const companyMenuBox = {
  cursor: 'pointer',
  display: 'inline-flex',
  button: {
    background: 'none',
    pl: '0px',
    ':hover': {
      background: 'none',
    },
  },
};

const filterMenuStyle = {
  gap: 'padding_16',
  justifyContent: 'space-between',
};

const keywordInputStyles = {
  maxWidth: '240px',
  flex: 1,
};

type Props = {
  setCompanyMenu: (value: boolean) => void;
  currentCompanyUuid: string;
  currentCompanyName: string;
  companyId: number;
  groupData: GroupRawResponse | null;
};

const CompanyProjectsView = ({
  setCompanyMenu,
  currentCompanyUuid,
  currentCompanyName,
  companyId,
  groupData,
}: Props) => {
  const [group] = useDashboardGroupId();
  const navigate = useNavigate();
  const [keywordFilter, setKeywordFilter] = useState('');
  const debouncedKeywordFilter = useDebounce(keywordFilter, 500);

  const { nextPage, previousPage, page: currentPage, setPageNumber } = usePaginate();
  const { fetchData: fetchProjectData, loading: projectCsvLoading } = useProjectCsvExport(
    {
      id: String(group),
      company: currentCompanyName,
      companyId: String(companyId),
    },
  );
  const { sortArray } = useSortedProjects();
  const { getFilteredLeads } = useFilterGroupConfigLeads({
    groupConfigId: Number(group),
    currentPage,
    sortArray,
    activeTabs: [],
    companyIds: [companyId],
    keywords: debouncedKeywordFilter,
  });
  const { data: projectData, isLoading: loading } = getFilteredLeads;
  const { getAllHiddenProjectIdsForUser } = useHiddenProjects();
  const { data: hiddenProjectsSet } = getAllHiddenProjectIdsForUser;
  const { filteredProjectData, hiddenProjectsCount } = useMemo(() => {
    let countHiddenProjects = 0;
    if (!projectData?.results) {
      return {
        filteredProjectData: [],
        hiddenProjectsCount: 0,
      };
    }

    const filteredList = projectData.results?.filter((item) => {
      if (hiddenProjectsSet?.has(item.slug)) {
        countHiddenProjects += 1;

        return false;
      }

      return true;
    });

    return {
      filteredProjectData: filteredList,
      hiddenProjectsCount: countHiddenProjects,
    };
  }, [projectData?.results, hiddenProjectsSet]);

  const totalProjectCount = useMemo(() => {
    if (!projectData?.count) {
      return 0;
    }

    return projectData.count - hiddenProjectsCount;
  }, [projectData?.count, hiddenProjectsCount]);

  const totalPages = (): number => {
    return Math.ceil(totalProjectCount / 20) || 1;
  };

  const displayHeading = `${totalProjectCount} active projects found`;

  const groupCityName = useMemo(() => {
    // Newly created Company Group Configurations will not have a city trigger
    // Just generate the name via the metro code
    const groupState = groupData?.state_code;
    const regionLabel = STATE_CODE_TO_LABEL[groupState!];

    return regionLabel;
  }, [groupData]);

  return (
    <FlexRow sx={containerRow}>
      <FlexColumn sx={containerColumn}>
        <FlexRow>
          <FlexRow.Left>
            <Box>
              <Box onClick={() => setCompanyMenu(false)} sx={companyMenuBox}>
                <Button sx={companyMenuBox.button}>
                  <Icon fill={Colors.TextQuiet} name={Icon.Name.ChevronLeft} />
                </Button>
                <Text.H3>{currentCompanyName}</Text.H3>
              </Box>
            </Box>
          </FlexRow.Left>
          <FlexRow.Right>
            <Button
              variant="primary"
              sx={buttonStyles}
              startAdornment={<Icon name={Icon.Name.Map} />}
              onClick={() =>
                navigate(buildProjectMapURLWithParams({ groupId: Number(group) }))
              }
            >
              View in map
            </Button>
            <Button
              variant="secondary"
              sx={buttonStyles}
              startAdornment={!projectCsvLoading && <Icon name={Icon.Name.Download} />}
              isLoading={projectCsvLoading}
              onClick={() => fetchProjectData()}
            >
              Export to CSV
            </Button>
            <Button
              variant="functional"
              onClick={() => {
                navigate(`/companies/${currentCompanyUuid}`);
              }}
            >
              View company profile
            </Button>
          </FlexRow.Right>
        </FlexRow>
        <FlexRow sx={filterMenuStyle}>
          <Text> Projects in {groupCityName}</Text>
          <Box sx={keywordInputStyles}>
            <SearchInput
              value={keywordFilter}
              onChange={setKeywordFilter}
              placeholder="Search projects by keyword"
            />
          </Box>
        </FlexRow>
        <DashboardLeadsTable
          items={filteredProjectData}
          leadContext={{
            groupId: String(group),
            groupType: ProjectGroupTypes.COMPANY_GROUP,
          }}
          containerSx={{ flex: '0 1 auto' }}
          isLoading={loading}
          header={displayHeading}
          footer={
            <Pagination
              currentPage={currentPage}
              isLoading={loading}
              setPage={(pageNumber) => setPageNumber(pageNumber)}
              totalPages={totalPages()}
              onNext={() => nextPage()}
              onPrev={() => previousPage()}
            />
          }
        />
      </FlexColumn>
    </FlexRow>
  );
};

export default CompanyProjectsView;
